import type { AccountType } from '../../types/account';

// ACCOUNT PAGE TAB TAXONOMY (Phase 1 accounts rework).
//
// The account VIEW (AccountDetail) and the EDIT form (AccountForm) share ONE
// tab vocabulary so navigating between viewing and editing stays consistent,
// and so a view→edit hand-off can preserve the tab the user was on. This module
// is the single source of that vocabulary + the pure visibility rules, reused by
// both surfaces (and unit-tested) so they can't drift apart.
//
// NB: this is the documented EXCEPTION to CLAUDE.md's "detail pages are
// section-based, not tabbed" rule — the account page outgrew a single scroll.
// The `Section` primitive is still used to group content WITHIN a tab.

export type AccountTabId =
  | 'details'
  | 'services'
  | 'contacts'
  | 'connections'
  | 'franchise'
  | 'dealership'
  | 'campaigns'
  | 'assets'
  | 'kyckconnect'
  | 'activity';

export const ACCOUNT_TABS: ReadonlyArray<{ id: AccountTabId; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'services', label: 'Services' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'connections', label: 'Connections' },
  { id: 'franchise', label: 'Franchise' },
  { id: 'dealership', label: 'Dealership' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'assets', label: 'Assets' },
  { id: 'kyckconnect', label: 'KyckConnect' },
  // id kept as 'activity' so existing ?tab=activity links keep working.
  { id: 'activity', label: 'History' },
];

const LABELS: Record<AccountTabId, string> = ACCOUNT_TABS.reduce(
  (acc, t) => ({ ...acc, [t.id]: t.label }),
  {} as Record<AccountTabId, string>,
);

export function accountTabLabel(id: AccountTabId): string {
  return LABELS[id];
}

// The Franchise tab only applies to account types that carry a franchise
// profile: a brand (FranchiseProfile) or a location (FranchiseLocationProfile).
// group / head_office / region / dealership never have one, so the tab is hidden
// rather than shown as an empty shell.
export function accountHasFranchiseTab(accountType: AccountType): boolean {
  return accountType === 'brand' || accountType === 'location';
}

// The Dealership tab (brand marque ref + website platform + CRM) applies ONLY to
// dealership accounts, mirroring how Franchise is type-scoped.
export function accountHasDealershipTab(accountType: AccountType): boolean {
  return accountType === 'dealership';
}

// The Assets tab (client-portal photos and videos, commit 13) applies only to
// OPERATIONAL accounts — an asset lives on a location or a dealership, never on
// a brand or group (PORTAL_OPERATIONAL_TYPES). View-only: nothing on it is part
// of the account form.
export function accountHasAssetsTab(accountType: AccountType): boolean {
  return accountType === 'location' || accountType === 'dealership';
}

// The KyckConnect tab (redesign Phase 1; it REPLACED the Leads tab, whose content is now its Leads sub-tab): a club's lead
// dashboard, leads, calls and setup checklist. Operational accounts only, like Assets: a lead belongs to a location or a
// dealership, never to a brand or group. View-only (its Setup editors save through their own endpoints, not the account form).
export function accountHasKyckConnectTab(accountType: AccountType): boolean {
  return accountType === 'location' || accountType === 'dealership';
}

// Tabs shown on the read-only VIEW. Everything applies to every account type
// except Franchise (type-scoped above). Connections is intentionally kept for
// all types: it always carries the live API-connections widget + any populated
// URLs, so it's never a true empty shell (a brand may still have a website /
// social URL we must not hide).
export function visibleViewTabIds(accountType: AccountType): AccountTabId[] {
  return ACCOUNT_TABS.map((t) => t.id).filter(
    (id) =>
      (id !== 'franchise' || accountHasFranchiseTab(accountType)) &&
      (id !== 'dealership' || accountHasDealershipTab(accountType)) &&
      (id !== 'assets' || accountHasAssetsTab(accountType)) &&
      (id !== 'kyckconnect' || accountHasKyckConnectTab(accountType)),
  );
}

// Tabs shown on the EDIT form. The form only edits Details / Services /
// Connections (+ Franchise for brand/location). Contacts / Campaigns / Activity
// are view-only surfaces with nothing to edit, so the form omits them rather
// than rendering empty shells — a view→edit hand-off on one of those tabs falls
// back to the first editable tab via resolveAccountTab.
export function visibleEditTabIds(accountType: AccountType): AccountTabId[] {
  const ids: AccountTabId[] = ['details', 'services', 'connections'];
  if (accountHasFranchiseTab(accountType)) ids.push('franchise');
  if (accountHasDealershipTab(accountType)) ids.push('dealership');
  return ids;
}

// Resolve a requested tab (from the URL) against the set actually visible for
// this account. Falls back to the first visible tab when the request is missing
// or not applicable (e.g. deep-linking ?tab=franchise on a group, or arriving on
// the edit form from a view-only tab).
export function resolveAccountTab(
  requested: string | null | undefined,
  visible: AccountTabId[],
): AccountTabId {
  // ⚠️ `?tab=leads` is the retired Leads tab: old bookmarks and links keep working by mapping to its replacement.
  const wanted = requested === 'leads' ? 'kyckconnect' : requested;
  const match = visible.find((id) => id === wanted);
  return match ?? visible[0] ?? 'details';
}
